import React from 'react'

/*
 * conditionsData — canonical reference for the 11 condition pages
 * surfaced from the home page "Expertise in mental wellness" section.
 *
 * Each entry drives:
 *   - the card shown in the home-page Conditions grid
 *   - the full /conditions/:slug page (hero, overview, signs, care
 *     philosophy, therapy role, medication role, self-test CTA, FAQ)
 *
 * Tone guidelines applied across every entry:
 *   - Empathetic, not dramatic. Describe rather than alarm.
 *   - Neutral between therapy and medication. Both have a role;
 *     the right plan depends on presentation, not ideology.
 *   - Plain-language first; clinical terms only when they clarify.
 *   - "Earlier is better" rather than "if things get really bad".
 *
 * `assessmentSlugs` correspond to server-side AssessmentTest docs
 * (the backend exposes them via /api/feature?feature=assessment&slug=…
 * for signed-in users). Self-report screeners only; full clinical
 * assessments are done by the care team in person.
 */

// Minimal, distinct SVG icons for each condition. Kept inline so we
// don't have to touch the shared icons.jsx file (which a lot of the
// marketing site relies on) and so each condition can have its own
// silhouette.

const iconBase = {
  width: 28,
  height: 28,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
}

const Icon = {
  Droplet: (p) => (
    <svg {...iconBase} {...p}>
      <path d="M12 2.5s6 7 6 11a6 6 0 0 1-12 0c0-4 6-11 6-11z" />
    </svg>
  ),
  Waves: (p) => (
    <svg {...iconBase} {...p}>
      <path d="M2 9c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 4 2 2-2 4-2" />
      <path d="M2 14c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 4 2 2-2 4-2" />
      <path d="M2 19c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 4 2 2-2 4-2" />
    </svg>
  ),
  Cloud: (p) => (
    <svg {...iconBase} {...p}>
      <path d="M18 10a5 5 0 0 0-9.58-1.5A4 4 0 0 0 7 16h11a4 4 0 0 0 0-6z" />
      <path d="M8 19v2" />
      <path d="M12 19v3" />
      <path d="M16 19v2" />
    </svg>
  ),
  Zigzag: (p) => (
    <svg {...iconBase} {...p}>
      <polyline points="3 16 7 8 11 16 15 8 19 16 21 12" />
    </svg>
  ),
  Repeat: (p) => (
    <svg {...iconBase} {...p}>
      <polyline points="17 1 21 5 17 9" />
      <path d="M3 11V9a4 4 0 0 1 4-4h14" />
      <polyline points="7 23 3 19 7 15" />
      <path d="M21 13v2a4 4 0 0 1-4 4H3" />
    </svg>
  ),
  Pill: (p) => (
    <svg {...iconBase} {...p}>
      <rect x="2.5" y="9" width="19" height="6" rx="3" />
      <line x1="12" y1="9" x2="12" y2="15" />
    </svg>
  ),
  Brain: (p) => (
    <svg {...iconBase} {...p}>
      <path d="M9 3a3 3 0 0 0-3 3v2a3 3 0 0 0-3 3 3 3 0 0 0 3 3v2a3 3 0 0 0 6 0V3z" />
      <path d="M15 3a3 3 0 0 1 3 3v2a3 3 0 0 1 3 3 3 3 0 0 1-3 3v2a3 3 0 0 1-6 0V3z" />
    </svg>
  ),
  Loop: (p) => (
    <svg {...iconBase} {...p}>
      <path d="M21 12a9 9 0 1 1-3-6.7" />
      <polyline points="21 3 21 9 15 9" />
    </svg>
  ),
  Puzzle: (p) => (
    <svg {...iconBase} {...p}>
      <path d="M10 3h4a1 1 0 0 1 1 1v2a2 2 0 0 0 4 0 1 1 0 0 1 1 1v4a1 1 0 0 1-1 1 2 2 0 0 0 0 4 1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1 2 2 0 0 0-4 0 1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1 2 2 0 0 0 0-4 1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h4a1 1 0 0 1 1-1V4a1 1 0 0 1 1-1z" />
    </svg>
  ),
  Clock: (p) => (
    <svg {...iconBase} {...p}>
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 16 14" />
    </svg>
  ),
  Heart: (p) => (
    <svg {...iconBase} {...p}>
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  ),
}

// ── condition records ──────────────────────────────────────────────

export const CONDITIONS = [
  {
    slug: 'depression',
    title: 'Depression',
    tagline: 'Persistent low mood that goes beyond a bad week',
    cardBlurb: 'Evidence-based therapy + psychiatric review when clinically indicated.',
    Icon: Icon.Cloud,
    accent: 'indigo',
    overview: [
      "Depression isn't a personal failing or a 'mood'. It's a clinical condition that changes how the brain regulates sleep, appetite, energy, motivation, and the capacity to feel pleasure — usually for weeks or months at a time.",
      "Most people with depression get meaningfully better with the right plan. The hard part is reaching out before the weight feels permanent.",
    ],
    signs: [
      'Persistent low mood, emptiness, or hopelessness',
      'Loss of interest in things you previously enjoyed',
      'Changes in sleep — sleeping too much or too little',
      'Changes in appetite or weight',
      'Fatigue that rest doesn\u2019t fix',
      'Difficulty concentrating or making decisions',
      'Feelings of guilt or worthlessness',
      'Thoughts of self-harm or not wanting to be here',
    ],
    whenToReachOut: [
      'Symptoms have lasted two weeks or more',
      'Work, school, or relationships are getting harder to keep up',
      'You\u2019re having thoughts of self-harm (urgent — please call a helpline or reach us immediately)',
    ],
    therapyRole:
      'Structured therapy — particularly Cognitive Behavioural Therapy (CBT), Behavioural Activation, and Interpersonal Therapy — has strong evidence for mild-to-moderate depression.',
    medicationRole:
      'Antidepressants are often appropriate for moderate-to-severe presentations, recurrent episodes, or when therapy alone isn\u2019t enough. They aren\u2019t addictive; they work by restoring the biology that supports mood.',
    assessmentSlugs: ['beck-depression-inventory'],
    faqs: [
      {
        q: 'Do I need medication?',
        a: 'Not necessarily. Mild depression often responds to therapy alone. Medication is typically added when symptoms are moderate-to-severe, when therapy plateaus, or when biology (sleep, appetite, energy) is clearly disrupted. The psychiatrist and you decide together.',
      },
      {
        q: 'How long does treatment take?',
        a: 'Most people notice meaningful change in 6–12 weeks of consistent care. Full recovery usually takes longer and often includes a stretch of maintenance therapy or medication to prevent relapse.',
      },
      {
        q: 'What if I feel better quickly and want to stop?',
        a: 'Stopping early is one of the biggest reasons depression returns. The care team tapers medication slowly and steps therapy down gradually — both on a clinical timeline, not a calendar.',
      },
    ],
  },

  {
    slug: 'anxiety',
    title: 'Anxiety',
    tagline: 'When worry becomes disproportionate to what\u2019s actually happening',
    cardBlurb: 'CBT and exposure-based care. Medication where symptoms are heavy.',
    Icon: Icon.Waves,
    accent: 'sky',
    overview: [
      'Anxiety disorders aren\u2019t \u201cjust stress\u201d. The nervous system gets stuck treating ordinary situations like emergencies — racing heart, shallow breathing, intrusive what-ifs — and avoidance slowly starts shaping day-to-day life.',
      'Generalised anxiety, panic disorder, social anxiety, and health anxiety share a core pattern: the worry feels protective, but it keeps making the world smaller.',
    ],
    signs: [
      'Persistent, hard-to-control worry',
      'Restlessness or a sense of being \u201con edge\u201d',
      'Muscle tension, headaches, jaw clenching',
      'Difficulty concentrating; mind going blank',
      'Irritability out of proportion to triggers',
      'Sleep disturbance (difficulty falling or staying asleep)',
      'Panic episodes — sudden spikes of fear with physical symptoms',
      'Avoidance of situations that trigger anxiety',
    ],
    whenToReachOut: [
      'Anxiety is interfering with work, school, or relationships',
      'You\u2019re avoiding things you\u2019d otherwise want to do',
      'You\u2019ve had a panic attack',
      'Sleep is being affected more than a couple of nights a week',
    ],
    therapyRole:
      'Cognitive Behavioural Therapy and exposure-based therapy are first-line for most anxiety disorders. They work by gradually teaching the nervous system that feared situations are safe, rather than avoiding them.',
    medicationRole:
      'SSRIs and SNRIs are often useful when symptoms are severe, when avoidance is significant, or when anxiety is co-occurring with depression. Short-term medication can help break an acute loop so therapy can take hold.',
    assessmentSlugs: ['gad-7'],
    faqs: [
      {
        q: 'Will therapy actually work if my anxiety has been around for years?',
        a: 'Yes — long-standing anxiety often responds well because the patterns are well-practised and therefore easy to map. The work is consistent rather than dramatic.',
      },
      {
        q: 'Are anxiety medications habit-forming?',
        a: 'SSRIs and SNRIs — the usual first-line medications — are not habit-forming. Short-term benzodiazepines are sometimes used carefully for acute panic, but they\u2019re not a long-term answer. The psychiatrist explains the plan.',
      },
    ],
  },

  {
    slug: 'ocd',
    title: 'OCD',
    tagline: 'Intrusive thoughts and rituals that take up hours of the day',
    cardBlurb: 'Exposure & Response Prevention is the gold-standard approach.',
    Icon: Icon.Loop,
    accent: 'violet',
    overview: [
      'Obsessive-Compulsive Disorder is a specific, treatable condition — not perfectionism or a preference for order. Unwanted thoughts (obsessions) produce distress; behaviours or mental rituals (compulsions) temporarily relieve it, which teaches the brain to repeat the cycle.',
      'OCD is under-diagnosed and over-mistaken for personality traits. Correctly identifying it changes the treatment plan entirely.',
    ],
    signs: [
      'Recurrent, intrusive thoughts, images, or urges you don\u2019t want',
      'Repetitive behaviours — checking, washing, counting, arranging',
      'Mental rituals — reviewing, praying silently, neutralising thoughts',
      'A sense of needing to do things \u201cjust right\u201d',
      'Time lost to rituals (often more than an hour a day)',
      'Significant distress when you resist the ritual',
      'Avoidance of situations that trigger obsessions',
    ],
    whenToReachOut: [
      'Rituals take more than an hour a day',
      'Obsessions are causing significant distress',
      'You\u2019re avoiding situations to prevent intrusive thoughts',
      'Family members are being drawn into reassurance or rituals',
    ],
    therapyRole:
      'Exposure and Response Prevention (ERP) is the most-studied, most-effective treatment for OCD. It works by helping you face triggers without performing the compulsion — teaching the brain that the distress is survivable and fades on its own.',
    medicationRole:
      'SSRIs — typically at higher doses than for depression — are well-established for moderate-to-severe OCD and are often used alongside ERP. The combination frequently outperforms either alone in more severe cases.',
    assessmentSlugs: ['obsessive-compulsive-test-y-bocs'],
    faqs: [
      {
        q: 'Is OCD just about being clean or organised?',
        a: 'No. The stereotype is misleading. OCD can involve harm-related, sexual, religious, relationship, or existential themes — all of them distressing and none of them character flaws. The thoughts are not wishes.',
      },
      {
        q: 'Does ERP mean I\u2019ll be forced into uncomfortable situations?',
        a: 'ERP is gradual and collaborative. You and the therapist build a ladder together and step up at a pace that works. The discomfort is real but deliberate, and it teaches the brain something nothing else does.',
      },
    ],
  },

  {
    slug: 'bipolar-disorder',
    title: 'Bipolar Disorder',
    tagline: 'Mood episodes that swing between elevated and low energy states',
    cardBlurb: 'Mood-stabilising treatment + structured psychoeducation and relapse-prevention.',
    Icon: Icon.Zigzag,
    accent: 'indigo',
    overview: [
      'Bipolar disorder is characterised by episodes of mania or hypomania — high energy, reduced need for sleep, rapid thoughts, sometimes poor judgment — alternating with depressive episodes. Between episodes, many people feel fine for weeks or years.',
      'It\u2019s often misread as \u201cmood swings\u201d or depression alone, which delays care. Early recognition and the right medication plan are the single biggest lever on long-term outcomes.',
    ],
    signs: [
      'Episodes of unusually elevated mood, energy, or activity',
      'Reduced need for sleep during high periods',
      'Racing thoughts or pressured speech',
      'Impulsive decisions — spending, travel, risky behaviour',
      'Irritability or agitation',
      'Depressive episodes that match the patterns of depression',
      'A family history of bipolar disorder',
    ],
    whenToReachOut: [
      'Mood episodes lasting days (manic) or weeks (depressive)',
      'Reckless decisions during high periods',
      'Suicidal thoughts during low periods',
      'A family member or partner has raised concerns',
    ],
    therapyRole:
      'Psychoeducation, CBT, and family-focused therapy support relapse prevention — they map triggers, protect sleep, and help family members recognise early warning signs. Therapy complements but does not replace medication.',
    medicationRole:
      'Mood-stabilising medication (lithium, valproate, lamotrigine, and certain antipsychotics) is the foundation of long-term bipolar care. Adherence is the single largest factor predicting how few episodes you experience over the years.',
    assessmentSlugs: ['the-mood-disorder-questionnaire', 'young-mania-rating-scale-ymrs'],
    faqs: [
      {
        q: 'Is medication lifelong?',
        a: 'For most people with bipolar I, long-term mood-stabilising medication reduces the frequency and severity of episodes dramatically. The psychiatrist revisits the plan periodically; any change is clinical, not automatic.',
      },
      {
        q: 'How do I know if it\u2019s bipolar or depression?',
        a: 'Depression looks the same in both. Bipolar adds episodes of elevated energy or reduced need for sleep — sometimes subtle. A psychiatrist reviews your history carefully because the treatment is different.',
      },
    ],
  },

  {
    slug: 'schizophrenia',
    title: 'Schizophrenia',
    tagline: 'A shift in how reality is experienced — treatable with early care',
    cardBlurb: 'Early psychiatric care dramatically improves long-term outcomes.',
    Icon: Icon.Brain,
    accent: 'sky',
    overview: [
      'Schizophrenia involves changes in thinking, perception, and behaviour — sometimes hallucinations (hearing or seeing things that others don\u2019t), delusions (firmly-held beliefs that don\u2019t match reality), disorganised thinking, or withdrawal from social life.',
      'The single most important factor in outcome is how soon treatment begins. \u201cFirst-episode\u201d care — started within months of symptoms emerging — is associated with significantly better long-term recovery than delayed treatment.',
    ],
    signs: [
      'Hearing voices or seeing things others don\u2019t',
      'Strong beliefs that don\u2019t match the evidence around you',
      'Disorganised or confusing speech',
      'Reduced expression, motivation, or self-care',
      'Social withdrawal or isolation',
      'Difficulty concentrating or following conversations',
      'Sleep disruption or changes in daily routine',
    ],
    whenToReachOut: [
      'As early as possible — days and weeks matter, not months',
      'A family member has noticed any of the above',
      'Someone is having thoughts of self-harm or harm to others',
    ],
    therapyRole:
      'Psychosocial interventions — CBT for psychosis, family interventions, social skills training, supported employment — work alongside medication to help rebuild day-to-day life and reduce relapse.',
    medicationRole:
      'Antipsychotic medication is central. Which one is used and at what dose is individualised. Adherence strongly predicts how well someone does over years — skipping doses is the most common reason relapses happen.',
    assessmentSlugs: [],
    faqs: [
      {
        q: 'Is schizophrenia a life sentence?',
        a: 'No. With consistent treatment many people work, maintain relationships, and live full lives. Outcomes vary, but early care plus sustained medication adherence shift the odds significantly.',
      },
      {
        q: 'What if the person doesn\u2019t believe they\u2019re unwell?',
        a: 'This is common and clinically recognised (it\u2019s called \u201canosognosia\u201d). The care team works with the family on engagement strategies — rarely with force. Every case is navigated carefully.',
      },
    ],
  },

  {
    slug: 'addiction',
    title: 'Addiction',
    tagline: 'Patterns that keep returning despite the cost',
    cardBlurb: 'Medical management, therapy, and relapse-prevention — integrated.',
    Icon: Icon.Repeat,
    accent: 'violet',
    overview: [
      'Substance Use Disorders are patterns of use that persist despite clear harm — physical, psychological, occupational, or relational. Tolerance (needing more to get the same effect) and withdrawal (feeling unwell without the substance) often develop and reinforce the cycle.',
      'The behaviour is not a moral failure. Every evidence-based treatment framework starts from that premise.',
    ],
    signs: [
      'Using more or for longer than you intended',
      'Repeated attempts to cut down that didn\u2019t work',
      'Spending significant time getting, using, or recovering',
      'Craving that intrudes on other thoughts',
      'Continued use despite clear harm to health or relationships',
      'Tolerance — needing more to get the same effect',
      'Withdrawal — feeling unwell when use stops',
      'Dropping activities that used to matter',
    ],
    whenToReachOut: [
      'The pattern has started to cost something — a relationship, work, health',
      'Attempts to stop haven\u2019t stuck',
      'Withdrawal symptoms have appeared',
    ],
    therapyRole:
      'CBT, Motivational Interviewing, and contingency-management approaches have strong evidence. Therapy builds the skills and self-awareness that medication alone can\u2019t.',
    medicationRole:
      'Relapse-prevention medication — naltrexone, buprenorphine, methadone, disulfiram — is well established for specific substances. For some people it\u2019s decisive. For others, therapy and recovery support are the primary tools. The plan is individualised.',
    assessmentSlugs: [],
    faqs: [
      {
        q: 'Do I need to go to rehab?',
        a: 'Not always. Out-patient care with structured therapy and, where appropriate, relapse-prevention medication works for many people. Residential care is one tool among several — the psychiatrist recommends based on severity and stability.',
      },
      {
        q: 'Is relapse a failure?',
        a: 'No. Relapse is common in the treatment of addictive disorders and the care plan expects it. The goal is learning faster from each episode, not never having one.',
      },
    ],
  },

  {
    slug: 'alcoholism',
    title: 'Alcoholism',
    tagline: 'When drinking patterns start shaping the rest of life',
    cardBlurb: 'Medically-supervised detox where needed, then sustained relapse-prevention.',
    Icon: Icon.Droplet,
    accent: 'indigo',
    overview: [
      'Alcohol Use Disorder ranges from mild to severe. It isn\u2019t about how much someone drinks but the pattern — loss of control, cravings, continued use despite harm, and the physical dependence that can develop with time.',
      'Alcohol is one of the few substances where withdrawal can be medically serious. Stopping suddenly after heavy, long-term use needs clinical supervision.',
    ],
    signs: [
      'Drinking more or longer than intended',
      'Unsuccessful attempts to cut down',
      'Strong craving for alcohol',
      'Continued drinking despite physical or relational harm',
      'Tolerance — needing more for the same effect',
      'Withdrawal symptoms — tremor, sweating, nausea, anxiety',
      'Drinking earlier in the day or in risky situations',
    ],
    whenToReachOut: [
      'Early. The mild stage responds fastest',
      'Any sign of withdrawal (tremor, sweating, morning drinking)',
      'A family member has raised concerns',
    ],
    therapyRole:
      'CBT, Motivational Interviewing, and relapse-prevention therapy provide the skills that keep recovery steady. Group support (AA or SMART Recovery) is a good complement, not a substitute.',
    medicationRole:
      'Naltrexone, acamprosate, and disulfiram are evidence-based. They reduce craving, support abstinence, or make drinking aversive — chosen based on the person\u2019s goals and history. Medically-supervised detox is sometimes the first step.',
    assessmentSlugs: [],
    faqs: [
      {
        q: 'Can I try to cut down rather than quit entirely?',
        a: 'For some presentations, controlled-drinking goals are clinically reasonable. For dependence or complications, abstinence is safer. The psychiatrist talks through the options with you.',
      },
      {
        q: 'Is withdrawal dangerous?',
        a: 'For heavy, long-standing drinkers, yes — seizures and delirium tremens are medical emergencies. That\u2019s why we plan detox carefully rather than suggesting you stop abruptly at home.',
      },
    ],
  },

  {
    slug: 'drug-addiction',
    title: 'Drug Addiction',
    tagline: 'When substance use keeps outpacing intention',
    cardBlurb: 'Integrated medical + psychological care tailored to the substance.',
    Icon: Icon.Pill,
    accent: 'violet',
    overview: [
      'Substance-specific addictions — opioids, stimulants, cannabis, benzodiazepines, prescription medications — all share the core pattern of a Substance Use Disorder but differ in withdrawal profile, medical risk, and the medications that help.',
      'The right care plan depends heavily on what\u2019s being used. \u201cAddiction\u201d isn\u2019t one condition — it\u2019s a family of them.',
    ],
    signs: [
      'Using more than intended',
      'Using earlier, more frequently, or for longer',
      'Craving and preoccupation',
      'Continued use despite harm',
      'Tolerance and/or withdrawal',
      'Important activities reduced or given up',
      'Social circle increasingly revolving around use',
    ],
    whenToReachOut: [
      'The pattern is starting to cost something',
      'Prescribed medications (e.g. painkillers, sedatives) have become hard to control',
      'Withdrawal is uncomfortable enough that use continues to avoid it',
    ],
    therapyRole:
      'CBT and Motivational Interviewing are strongly supported across substances. Contingency management (structured reinforcement for verified abstinence) has particularly good data for stimulants.',
    medicationRole:
      'For opioid dependence, buprenorphine and methadone are transformative — reducing cravings and mortality substantially. For stimulants, medication is adjunctive. The psychiatrist selects based on the substance and your history.',
    assessmentSlugs: [],
    faqs: [
      {
        q: 'Is medication just replacing one drug with another?',
        a: 'No. Medications like buprenorphine are long-acting and dosed to stabilise — they don\u2019t produce a cycle of intoxication. Decades of data show they save lives and support recovery.',
      },
    ],
  },

  {
    slug: 'autism-spectrum-disorder',
    title: 'Autism Spectrum Disorder',
    tagline: 'Different, not deficient — and specific support makes a real difference',
    cardBlurb: 'Thorough developmental assessment and personalised support plans.',
    Icon: Icon.Puzzle,
    accent: 'sky',
    overview: [
      'ASD is a developmental condition that shapes how a person communicates, processes sensory input, and relates socially. It is a spectrum — support needs vary widely. Many individuals live full, self-directed lives with the right accommodations; others benefit from more structured help.',
      'A diagnosis is not a label to shrink under. It\u2019s the key to the right supports, at the right time.',
    ],
    signs: [
      'Social communication differences — tone, eye contact, back-and-forth conversation',
      'Strong preference for routine; distress when routines change',
      'Intense focused interests',
      'Sensory sensitivities — sound, light, texture, crowds',
      'Literal interpretation of language',
      'Repetitive behaviours or movements',
      '(Childhood) delayed language or unusual developmental patterns',
    ],
    whenToReachOut: [
      'A child\u2019s development feels different from their peers in ways that persist',
      'An adult has always felt \u201cout of sync\u201d socially and wonders if there\u2019s a reason',
      'School, work, or relationships would benefit from targeted support',
    ],
    therapyRole:
      'Evidence-based interventions include speech & language support, occupational therapy for sensory regulation, and skills-focused behavioural therapy. For adolescents and adults, therapy often focuses on social scripts, self-advocacy, and managing co-occurring anxiety or depression.',
    medicationRole:
      'ASD itself isn\u2019t treated with medication. Medication may help co-occurring conditions — anxiety, ADHD, depression, sleep — that are common and that respond in the usual ways.',
    assessmentSlugs: ['indian-scale-for-assessment-of-autism-isaa'],
    faqs: [
      {
        q: 'Is late-in-life diagnosis meaningful?',
        a: 'Yes. Many adults find that a diagnosis reframes their whole history and unlocks accommodations, community, and targeted support. It\u2019s not about changing who you are.',
      },
      {
        q: 'My child was diagnosed — will therapy try to make them \u201cless autistic\u201d?',
        a: 'No. Quality intervention builds skills and regulation, not masking. The goal is a thriving, self-aware person — not one who passes as neurotypical at a cost to themselves.',
      },
    ],
  },

  {
    slug: 'dementia',
    title: 'Dementia',
    tagline: 'Thinking and memory changes that go beyond normal aging',
    cardBlurb: 'Early assessment rules out reversible causes and sets a long-term care plan.',
    Icon: Icon.Clock,
    accent: 'sky',
    overview: [
      'Dementia is an umbrella term for progressive conditions that affect memory, thinking, behaviour, and day-to-day function. Alzheimer\u2019s disease is the most common type but there are several others, each with slightly different patterns.',
      'A number of causes are reversible — thyroid dysfunction, B12 deficiency, depression, medication side-effects. The first step is always ruling those out. Only then is the longer-term picture clarified.',
    ],
    signs: [
      'Memory loss that starts affecting daily life',
      'Difficulty with familiar tasks — cooking, paying bills',
      'Confusion about time or place',
      'Trouble finding words or following conversation',
      'Misplacing things and struggling to retrace steps',
      'Poor judgment or out-of-character decisions',
      'Withdrawal from work or social life',
      'Mood or personality changes',
    ],
    whenToReachOut: [
      'Changes have persisted for months, not days',
      'A family member has noticed any of the above',
      'A recent hospital admission, fall, or significant illness has shifted things',
    ],
    therapyRole:
      'Cognitive stimulation therapy, reminiscence therapy, and caregiver education improve quality of life and slow decline in function. Support for the family is as important as support for the person.',
    medicationRole:
      'Cholinesterase inhibitors and memantine can slow progression in specific types of dementia. Medication for co-occurring mood disturbance or agitation is used carefully. Reviewing existing medications (polypharmacy) is often high-yield.',
    assessmentSlugs: [],
    faqs: [
      {
        q: 'Is forgetfulness always the first sign?',
        a: 'Not always. Some dementias start with personality or behaviour changes, language difficulty, or visuospatial trouble. That\u2019s why a full assessment matters — not just a memory quiz.',
      },
      {
        q: 'What about the caregiver?',
        a: 'Caregiver well-being is a clinical priority, not an afterthought. The plan explicitly includes support, respite, and education for family.',
      },
    ],
  },

  {
    slug: 'personality-disorder',
    title: 'Personality Disorder',
    tagline: 'Longstanding patterns that cause distress or affect relationships',
    cardBlurb: 'Evidence-based structured therapy — DBT, MBT, Schema — with the right fit.',
    Icon: Icon.Heart,
    accent: 'violet',
    overview: [
      'Personality disorders are enduring patterns of thinking, feeling, and relating that differ significantly from cultural expectations and cause distress or interpersonal difficulty. Borderline, avoidant, narcissistic, obsessive-compulsive, antisocial — each has a distinct presentation.',
      'The label carries stigma it doesn\u2019t deserve. Evidence-based therapy works — often dramatically — for many personality disorders, though progress is measured in months and years rather than weeks.',
    ],
    signs: [
      'Recurring difficulty in relationships',
      'Intense, shifting emotions',
      'Impulsivity — spending, self-harm, substance use, binge behaviours',
      'A fragile or unclear sense of self',
      'Fear of abandonment or rejection',
      'Repeated pattern of similar interpersonal conflicts',
      'Perfectionism or rigidity that causes suffering',
    ],
    whenToReachOut: [
      'Relationship patterns keep repeating despite good intentions',
      'Self-harm or suicidal thoughts have appeared',
      'Substance use is filling an emotional gap',
      'You\u2019ve been given (or suspect) a personality disorder diagnosis and want clarity',
    ],
    therapyRole:
      'Structured, long-term therapy is the primary treatment. Dialectical Behavioural Therapy (DBT) for borderline presentations, Mentalisation-Based Therapy (MBT), Schema Therapy, and Transference-Focused Psychotherapy all have strong evidence.',
    medicationRole:
      'There is no medication for personality disorder itself. Medication may address specific symptoms — depression, anxiety, intense mood swings, impulsivity — and often does so meaningfully, but it supports rather than replaces therapy.',
    assessmentSlugs: [],
    faqs: [
      {
        q: 'Isn\u2019t this a permanent label?',
        a: 'No. Modern evidence shows that personality disorder patterns change substantially with the right therapy over time. \u201cEnduring\u201d doesn\u2019t mean immovable.',
      },
      {
        q: 'How long is therapy?',
        a: 'Structured therapies typically run 12–24 months, sometimes longer. The commitment is real — and so are the outcomes in people who stay.',
      },
    ],
  },
]

export function getConditionBySlug(slug) {
  if (!slug) return null
  const s = String(slug).trim().toLowerCase()
  return CONDITIONS.find((c) => c.slug === s) || null
}

// Optional grouping used by the home-page showcase if we want to
// present conditions as clusters. Kept here so the list stays in one
// file rather than drifting between sections.
export const CONDITION_GROUPS = [
  {
    label: 'Mood',
    slugs: ['depression', 'bipolar-disorder'],
  },
  {
    label: 'Anxiety & OCD',
    slugs: ['anxiety', 'ocd'],
  },
  {
    label: 'Psychosis & cognition',
    slugs: ['schizophrenia', 'dementia'],
  },
  {
    label: 'Substance use',
    slugs: ['addiction', 'alcoholism', 'drug-addiction'],
  },
  {
    label: 'Developmental & personality',
    slugs: ['autism-spectrum-disorder', 'personality-disorder'],
  },
]
